import {
  arrayOf,
  element,
  func,
  shape,
  string,
  node,
  number,
  bool
} from 'prop-types';
import React from 'react';
import dynamic from 'next/dynamic';
import classnames from 'classnames';
import {
  HAS_PRISM,
  HAS_RECIPE_BOX,
  SPONSORED_HEADER_IMAGES
} from 'constants/site';
import { BASE_AD_UNITS } from 'constants/ads/units';
import { ALT_AFFILIATE_DISCLAIMER_SLUG } from 'constants/tags';
import AffiliateMessage from 'components/AffiliateMessage';
import Comment from 'components/Icons/Comment';
import Recipe from 'components/Icons/Recipe';
import RecipeRoundupJsonLd from 'components/RecipeRoundupJsonLd';
import PageActions from 'components/PageActions';
import PostBundleItem from 'components/PostBundleItem';
import PostKicker from 'components/PostKicker';
import PostTags from 'components/PostTags';
import PublishByline from 'components/PublishByLine';
import SaveStoryAction from 'components/SaveStoryAction';
import BundleSponsor from 'components/BundleSponsor';
import RecipePrintButton from 'components/RecipePrintButton';
import Ad from 'components/Ad';
import PostBylineBottom from 'components/PostBylineBottom';
import { getPathFromCanonical } from 'util/link';
import { hasTermWithSlug, findTermWithSponsoredSubtitle } from 'util/terms';
import siteStyles from 'styles/PostBundle.js';
import { createAddPostDataToItem } from './helpers';
import whitelist from '../../whitelist.json';
import SponsoredHeaderImage from '../SponsoredHeaderImage';

const SaveToRecipeBoxActions = dynamic(
  () => import('components/SaveToRecipeBoxActions'),
  {
    ssr: false
  }
);

/**
 * outputs a fully formatted post (or post-like content) with its above, below,
 * and aside (Rail) items.
 *
 * CLEANUP: figure out better way to output post-like content (WP pages) without
 * doing all the isPost logic.
 *
 */
export function PostBundle(props) {
  const {
    items: unfilteredItems,
    aside: unfilteredAside,
    above: unfilteredAbove,
    below: unfilteredBelow,
    recirc,
    data,
    extraArticleClasses,
    itemPropMapperAdditions,
    coverMedia,
    isPost,
    comments,
    isAffiliate,
    isSponsored,
    hideAds,
    hideComments,
    hidePrism,
    hideAccountActions,
    isAmp,
    additionalAffiliateText,
    wrapperRef
  } = props;
  const {
    canonical,
    title,
    author,
    publishedAt,
    updatedAt,
    categories,
    resourceId,
    tags,
    sponsor,
    pinterestBoardImage,
    pinterestPostTitle,
    ads
  } = data;

  // we apparently receive some 'empty' items, so remove them first before any
  // logic is applied.
  //
  // CLEANUP: investigate filtering these higher-up (oxygen-gql? page-bundler?
  // post-bundler?)
  const items = unfilteredItems ? unfilteredItems.filter(Boolean) : [];
  const aside = unfilteredAside ? unfilteredAside.filter(Boolean) : [];
  const above = unfilteredAbove ? unfilteredAbove.filter(Boolean) : [];
  const below = unfilteredBelow ? unfilteredBelow.filter(Boolean) : [];

  const hasPost = items && items.length > 0;
  const hasAside = aside && aside.length > 0;
  const hasAbove = above && above.length > 0;
  const hasBelow = below && below.length > 0;
  // Posts with recipe embeds include extra actions
  const inPostRecipeItem = hasPost && items.find(i => i.type === 'recipe');

  // Lookup for Sponsored Headers based on the current tags of the post
  const sponsoredTags = tags.filter(t =>
    (Object.keys(SPONSORED_HEADER_IMAGES) || []).includes(t.slug)
  );
  const sponsoredHeaderImg =
    sponsoredTags && sponsoredTags.length
      ? SPONSORED_HEADER_IMAGES[sponsoredTags[0].slug]
      : null;

  const bundleClasses = classnames('PostBundle', 'PostBundle--pov-recirc', {
    'PostBundle--with-rail': hasAside,
    'PostBundle--with-cover': coverMedia,
    'PostBundle--with-sponsored-header-image': !!sponsoredHeaderImg
  });
  // CLEANUP: just make it the standard .Post styling
  const postClasses = classnames(
    'Post',
    'Post--withToolbar',
    ...extraArticleClasses,
    {
      'Post--cake': isSponsored,
      'Post--withRecipe': !!inPostRecipeItem
    }
  );

  const addPostDataToItem = createAddPostDataToItem(props);

  // merge the result of itemPropMapperAdditions with the result of
  // addPostDataToItem(), along with other custom props
  const customPropsMapper = (...args) => ({
    ...addPostDataToItem(...args),
    ...itemPropMapperAdditions(...args),
    hasPrism: HAS_PRISM && !hidePrism
  });

  const showComments = hideComments === false;

  const pageActionProps = {
    resourceId,
    postImage: pinterestBoardImage,
    postUrl: canonical,
    postTitle: pinterestPostTitle || title,
    // Conditionally disable comments (AmpPageActions)
    disable: [hideComments ? 'comment' : ''],
    isAmp
  };

  // if post is tagged `Encyclopedia of Houseplants` (slug: encyclopedia-of-houseplants)
  // set a link prop for the licker to lead to the houseplant encyclopedia page
  const plantEncyclopediaLinkProp = hasTermWithSlug(
    tags,
    'encyclopedia-of-houseplants'
  )
    ? {
        href: '/features/houseplant-guide-encyclopedia',
        as: '/features/houseplant-guide-encyclopedia',
        title: 'Encyclopedia of Houseplants'
      }
    : undefined;

  // concat all roundup items together so that we can render them all in one JSON+LD block
  const recipeRoundupItems = items
    .filter(item => item.type === 'recipeCollection')
    .filter(item => item.data.recipes !== null)
    .reduce((accumulator, item) => accumulator.concat(item.data.recipes), []);

  // Add backward compatibility with legacy sponsored posts
  const termsWithSponsoredSubtitle = findTermWithSponsoredSubtitle(tags);

  return (
    /**
     * we need gtm-page-path on the div that wraps the post bundle content so
     * that we can attribute any interaction with child elements to the post's
     * path in GA. see getAssociatedPagePath variable in GTM.
     */
    <div
      className={bundleClasses}
      data-gtm-page-path={getPathFromCanonical(canonical)}
      {...(wrapperRef && {
        ref: wrapperRef
      })}
    >
      {hasAbove && (
        <div className="PostBundle__above">
          {above.map((item, idx) => (
            <PostBundleItem
              key={item.id}
              idx={idx}
              item={item}
              customPropsMapper={addPostDataToItem}
              isAmp={isAmp}
            />
          ))}
        </div>
      )}

      {sponsoredHeaderImg && (
        <div className="Post__sponsoredHeaderImage">
          <SponsoredHeaderImage header={sponsoredHeaderImg} isAmp={isAmp} />
        </div>
      )}

      {/*
       * we're adding data-gtm-post-id to each <article> so that we can do
       * things in GTM like track visibility of each post's content,
       * particularly when multiple posts occur in the same DOM.
       */}
      <article className={postClasses} data-gtm-post-id={resourceId}>
        {coverMedia && <div className="Post__cover">{coverMedia}</div>}
        <div className="Post__kickerTags">
          {!isSponsored &&
            !sponsoredHeaderImg &&
            (sponsor && !sponsor.name) && (
              <PostKicker
                categories={categories}
                tags={tags}
                whitelist={whitelist || undefined}
                link={plantEncyclopediaLinkProp}
              />
            )}
          <PostTags tags={tags} whitelist={whitelist || undefined} />
          {isSponsored && (
            <div className="Post__cake-tag">
              {sponsor && (
                <BundleSponsor
                  cssHook="Post"
                  name={sponsor.name}
                  messaging={sponsor.messaging}
                />
              )}
              {termsWithSponsoredSubtitle && (
                <div className="Post__cake-tag">
                  <BundleSponsor
                    cssHook="Post"
                    name={termsWithSponsoredSubtitle.name.replace(
                      'subtitle:',
                      ''
                    )}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <h1
          className="Post__headline"
          dangerouslySetInnerHTML={{ __html: title }}
        />
        {isPost && (
          <PublishByline
            publishedAt={publishedAt}
            updatedAt={updatedAt}
            author={author}
            isAmp={isAmp}
          />
        )}
        {isPost &&
          (inPostRecipeItem && HAS_RECIPE_BOX ? (
            <div className="Post__saveToRecipeBoxActions">
              {isPost && (
                <PageActions placement="post-top" {...pageActionProps} />
              )}
              <SaveToRecipeBoxActions
                recipeId={inPostRecipeItem.data.recipeId}
                postId={pageActionProps.resourceId}
                postUrl={pageActionProps.postUrl}
                gtmAttrs={{ placement: 'Recipe Page' }}
                isSticky
              />
            </div>
          ) : (
            <PageActions placement="post-top" {...pageActionProps} />
          ))}
        {isAmp === false && isPost && (
          <div className="Post__toolbar Post__toolbar--top">
            {hideAccountActions === false && !inPostRecipeItem && (
              <SaveStoryAction resourceId={resourceId} />
            )}
            {showComments && (
              <a
                className="Post__jumpToComments SitePostActions__comment-count-action"
                href={`#comments-${resourceId}`}
                data-gtm-placement="post-top"
                data-gtm-social-network="community"
              >
                <Comment display="inline-block" isOutlined />
                Comments
              </a>
            )}
            {inPostRecipeItem && (
              <a
                className="Post__jumpToRecipeButton"
                data-gtm-placement="post-top"
                href={`#post-recipe-${inPostRecipeItem.data.recipeId}`}
              >
                <Recipe display="inline-block" />
                Jump to Recipe
              </a>
            )}
            {inPostRecipeItem && (
              <RecipePrintButton
                {...inPostRecipeItem.data}
                placement="post-top"
              />
            )}
          </div>
        )}
        {(isAffiliate || additionalAffiliateText) && (
          <div className="Post__affiliate-slot">
            {isAffiliate && (
              <AffiliateMessage
                shouldShowAltDisclaimer={hasTermWithSlug(
                  tags,
                  ALT_AFFILIATE_DISCLAIMER_SLUG
                )}
              />
            )}
            {additionalAffiliateText}
          </div>
        )}
        {hasPost &&
          items.map((item, idx) => {
            // data can sometimes be null, so it doesn't get assigned the
            // default value above.
            const placement = item.data && item.data.placement;

            const divClasses = classnames('Post__item', `Post__${item.type}`, {
              // treat Headings and listicles also require the `Post__html` class
              Post__html: ['heading', 'listicle', 'sideBySide'].includes(
                item.type
              ),
              [`Post__${placement}`]: placement,
              'Post__item--first': idx === 0
            });

            return (
              <div
                key={item.id}
                className={divClasses}
                id={
                  item.type === 'recipe'
                    ? `post-recipe-${inPostRecipeItem.data.recipeId}`
                    : undefined
                }
              >
                <PostBundleItem
                  idx={idx}
                  item={item}
                  customPropsMapper={customPropsMapper}
                  isAmp={isAmp}
                />
              </div>
            );
          })}

        {isPost && (
          <PostBylineBottom
            author={author}
            isAmp={isAmp}
            isSponsored={isSponsored}
          />
        )}

        {isPost && !isAmp && !(hideAds || isSponsored) && (
          <div className="Post__item Post__ad">
            <Ad
              targeting={ads.targeting}
              unit={`${BASE_AD_UNITS.post}/Sponsored`}
              sizes={[[1, 1]]}
            />
          </div>
        )}

        <div className="Post__bottomActions">
          {isPost && (
            <PageActions placement="post-bottom" {...pageActionProps} />
          )}
        </div>
        {isAmp === false && isPost && (
          <div className="Post__toolbar Post__toolbar--bottom">
            {hideAccountActions === false && (
              <SaveStoryAction resourceId={resourceId} />
            )}
            {showComments && (
              <a
                className="Post__jumpToComments SitePostActions__comment-count-action"
                href={`#comments-${resourceId}`}
                data-gtm-placement="post-bottom"
                data-gtm-social-network="community"
              >
                <Comment display="inline-block" isOutlined />
                Comments
              </a>
            )}
            {inPostRecipeItem && (
              <a
                className="Post__jumpToRecipeButton"
                data-gtm-placement="post-bottom"
                href={`#post-recipe-${inPostRecipeItem.data.recipeId}`}
              >
                <Recipe display="inline-block" />
                Jump to Recipe
              </a>
            )}

            {inPostRecipeItem && (
              <RecipePrintButton
                {...inPostRecipeItem.data}
                placement="post-bottom"
              />
            )}
          </div>
        )}
        {hasAside && (
          <div className="Post__Rail">
            {aside.map((item, idx) => (
              <PostBundleItem
                key={item.id}
                idx={idx}
                item={item}
                customPropsMapper={addPostDataToItem}
                isAmp={isAmp}
              />
            ))}
          </div>
        )}
      </article>
      {isPost && recirc && <div className="Post__recirc-band">{recirc}</div>}
      {isPost && showComments && comments && (
        <div id={`comments-${resourceId}`} className="PostComments">
          {comments}
        </div>
      )}

      {hasBelow && (
        <div className="PostBundle__below">
          {below.map((item, idx) => (
            <PostBundleItem
              key={item.id}
              idx={idx}
              item={item}
              customPropsMapper={addPostDataToItem}
              isAmp={isAmp}
            />
          ))}
        </div>
      )}
      <RecipeRoundupJsonLd recipes={recipeRoundupItems} />
      <style jsx>{siteStyles}</style>
    </div>
  );
}

PostBundle.propTypes = {
  flips: arrayOf(string),

  data: shape({
    resourceId: string.isRequired,
    title: string.isRequired,
    author: shape({
      name: string.isRequired,
      imageUrl: string,
      authorUrl: string
    }).isRequired,
    categories: arrayOf(
      shape({
        name: string,
        slug: string
      })
    ).isRequired,
    tags: arrayOf(
      shape({
        name: string,
        slug: string
      })
    ).isRequired,
    publishedAt: number,
    updatedAt: number,
    sponsor: shape({
      name: string,
      messaging: string
    }),
    pinterestBoardImage: string
  }).isRequired,

  coverMedia: element,

  items: arrayOf(
    shape({
      id: string.isRequired,
      type: string.isRequired,
      data: shape({
        // TBD: based on item data types
      })
    })
  ).isRequired,

  // same as items
  aside: arrayOf(
    shape({
      id: string.isRequired,
      type: string.isRequired,
      data: shape({
        // TBD: based on item data types
      })
    })
  ),

  // same as items
  above: arrayOf(
    shape({
      id: string.isRequired,
      type: string.isRequired,
      data: shape({
        // TBD: based on item data types
      })
    })
  ),

  // same as items
  below: arrayOf(
    shape({
      id: string.isRequired,
      type: string.isRequired,
      data: shape({
        // TBD: based on item data types
      })
    })
  ),

  recirc: element,

  extraArticleClasses: arrayOf(string),
  itemPropMapperAdditions: func,
  isPost: bool,

  comments: node,

  hideAds: bool.isRequired,
  hideComments: bool.isRequired,
  hidePrism: bool.isRequired,
  hideAccountActions: bool.isRequired,
  isAffiliate: bool.isRequired,
  isDraft: bool.isRequired,
  isNoIndex: bool.isRequired,
  isSponsored: bool.isRequired,
  isAmp: bool,
  additionalAffiliateText: node,
  wrapperRef: shape({
    //
  })
};

PostBundle.defaultProps = {
  aside: undefined,
  above: undefined,
  below: undefined,
  flips: [],
  recirc: undefined,
  comments: undefined,
  extraArticleClasses: [],
  itemPropMapperAdditions: () => {},
  coverMedia: undefined,
  isPost: true,
  isAmp: false,
  additionalAffiliateText: undefined,
  wrapperRef: undefined
};

export default PostBundle;
